import { useEffect, useRef, useState, useCallback } from "react";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

/**
 * PinchZoomStage
 * --------------
 * Wraps arbitrary children (e.g. the 3D book) and adds:
 *   • Pinch-to-zoom (2-finger gesture)
 *   • Double-tap to toggle 2× zoom at the tap point
 *   • Pan (single-finger drag) ONLY when zoomed in (so the host's
 *     own single-touch handlers — like react-pageflip swipe —
 *     keep working at scale=1)
 *   • Ctrl+wheel desktop zoom
 *   • Floating zoom controls when zoomed
 *
 * Children stay interactive at scale=1; the wrapper becomes
 * a capturing layer only when scale > 1.
 */

const MIN_SCALE = 1;
const MAX_SCALE = 3;
const DOUBLE_TAP_MS = 280;
const DOUBLE_TAP_DIST = 30;

export default function PinchZoomStage({ children, className = "" }) {
  // Phones: bypass the pinch-zoom layer entirely. Mobile browsers already
  // give users their own zoom; our wrapper was hijacking 2-finger pinches
  // and triggering a 199% layout-breaking scale (see user video). We pass
  // children straight through so react-pageflip's own gesture engine is
  // the only thing listening to touches on phones.
  // Bypass on phone-class viewports — phone-portrait (w<640) AND
  // phone-landscape (h<500). In landscape the width crosses 640 but the
  // device is still a phone and the native gesture engine of react-pageflip
  // should be the only thing handling touches.
  const isPhoneViewport = () =>
    typeof window !== "undefined" &&
    (window.innerWidth < 640 || window.innerHeight < 500);
  const [isPhone, setIsPhone] = useState(isPhoneViewport);
  useEffect(() => {
    const onR = () => setIsPhone(isPhoneViewport());
    window.addEventListener("resize", onR);
    window.addEventListener("orientationchange", onR);
    return () => {
      window.removeEventListener("resize", onR);
      window.removeEventListener("orientationchange", onR);
    };
  }, []);

  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  // --- refs for gesture state (avoid re-renders during gesture) ---
  const gesture = useRef({
    mode: null, // 'pinch' | 'pan' | null
    startDist: 0,
    startScale: 1,
    startTx: 0,
    startTy: 0,
    startCx: 0,
    startCy: 0,
    lastTapTime: 0,
    lastTapX: 0,
    lastTapY: 0,
  });

  // Keep state snapshot accessible in listeners
  const stateRef = useRef({ scale: 1, tx: 0, ty: 0 });
  useEffect(() => {
    stateRef.current = { scale, tx, ty };
  }, [scale, tx, ty]);

  // Clamp translation so content never leaves the container
  const clamp = useCallback((nx, ny, s) => {
    const el = containerRef.current;
    if (!el) return { nx, ny };
    const w = el.clientWidth;
    const h = el.clientHeight;
    const maxX = ((s - 1) * w) / 2;
    const maxY = ((s - 1) * h) / 2;
    return {
      nx: Math.max(-maxX, Math.min(maxX, nx)),
      ny: Math.max(-maxY, Math.min(maxY, ny)),
    };
  }, []);

  const zoomAt = useCallback(
    (newScale, focalX, focalY) => {
      const el = containerRef.current;
      if (!el) return;
      const bounds = el.getBoundingClientRect();
      // Focal point relative to container center
      const fx = focalX - (bounds.left + bounds.width / 2);
      const fy = focalY - (bounds.top + bounds.height / 2);
      const { scale: s, tx: oldTx, ty: oldTy } = stateRef.current;
      const clampedScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
      // Adjust translation so the focal point stays stable
      const k = clampedScale / s;
      const newTx = fx + (oldTx - fx) * k;
      const newTy = fy + (oldTy - fy) * k;
      const { nx, ny } = clamp(newTx, newTy, clampedScale);
      setScale(clampedScale);
      setTx(clampedScale <= 1 ? 0 : nx);
      setTy(clampedScale <= 1 ? 0 : ny);
    },
    [clamp]
  );

  const reset = useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, []);

  // --- Touch handlers ---
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const getDistance = (t1, t2) => {
      const dx = t1.clientX - t2.clientX;
      const dy = t1.clientY - t2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = (e) => {
      const g = gesture.current;
      if (e.touches.length === 2) {
        // begin pinch
        g.mode = "pinch";
        g.startDist = getDistance(e.touches[0], e.touches[1]);
        g.startScale = stateRef.current.scale;
        g.startCx =
          (e.touches[0].clientX + e.touches[1].clientX) / 2;
        g.startCy =
          (e.touches[0].clientY + e.touches[1].clientY) / 2;
        e.preventDefault();
      } else if (e.touches.length === 1) {
        // only capture pan when already zoomed
        if (stateRef.current.scale > 1) {
          g.mode = "pan";
          g.startCx = e.touches[0].clientX;
          g.startCy = e.touches[0].clientY;
          g.startTx = stateRef.current.tx;
          g.startTy = stateRef.current.ty;
          e.preventDefault();
        }
        // NOTE: Double-tap-to-zoom is intentionally disabled — it was
        // hijacking taps on verses inside the 3D book. Use the pinch
        // gesture or the ⊕/⊖ controls on screen instead.
      }
    };

    const onTouchMove = (e) => {
      const g = gesture.current;
      if (g.mode === "pinch" && e.touches.length === 2) {
        const d = getDistance(e.touches[0], e.touches[1]);
        const ratio = d / g.startDist;
        const newScale = Math.max(
          MIN_SCALE,
          Math.min(MAX_SCALE, g.startScale * ratio)
        );
        zoomAt(newScale, g.startCx, g.startCy);
        e.preventDefault();
      } else if (g.mode === "pan" && e.touches.length === 1) {
        const dx = e.touches[0].clientX - g.startCx;
        const dy = e.touches[0].clientY - g.startCy;
        const { nx, ny } = clamp(
          g.startTx + dx,
          g.startTy + dy,
          stateRef.current.scale
        );
        setTx(nx);
        setTy(ny);
        e.preventDefault();
      }
    };

    const onTouchEnd = () => {
      gesture.current.mode = null;
    };

    // Desktop: Ctrl + wheel = zoom
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const delta = -e.deltaY * 0.005;
      const newScale = stateRef.current.scale * (1 + delta);
      zoomAt(newScale, e.clientX, e.clientY);
    };

    // Desktop: double-click = toggle zoom.
    // Only intercept on desktop when already zoomed, OR when the user
    // double-clicks OUTSIDE an actual book page (so we never fight
    // react-pageflip's own click-to-flip on pages at scale=1).
    const onDoubleClick = (e) => {
      if (stateRef.current.scale > 1) {
        reset();
        return;
      }
      // At scale=1: skip if the target is inside a flipbook page
      const insidePage =
        e.target instanceof Element &&
        (e.target.closest(".book-page-paper") ||
          e.target.closest(".book-page-inner"));
      if (insidePage) return;
      zoomAt(2, e.clientX, e.clientY);
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("dblclick", onDoubleClick);

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("dblclick", onDoubleClick);
    };
  }, [zoomAt, reset, clamp]);

  const zoomed = scale > 1.001;

  // Phone: hand children directly to react-pageflip. No transform layer,
  // no touch capture, no zoom controls. The mobile browser's native zoom
  // remains available if the user really needs it.
  if (isPhone) {
    return (
      <div className={`pinch-zoom-stage ${className}`} data-testid="pinch-zoom-stage" data-phone="true">
        {children}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`pinch-zoom-stage ${zoomed ? "is-zoomed" : ""} ${className}`}
      data-testid="pinch-zoom-stage"
      style={{ touchAction: zoomed ? "none" : "pan-y" }}
    >
      <div
        ref={contentRef}
        className="pinch-zoom-content"
        style={{
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
          transformOrigin: "center center",
          transition: gesture.current.mode ? "none" : "transform 180ms ease-out",
          willChange: "transform",
        }}
      >
        {children}
      </div>

      {zoomed && (
        <div className="pinch-zoom-controls" data-testid="pinch-zoom-controls">
          <button
            type="button"
            aria-label="הקטן"
            onClick={() => {
              const el = containerRef.current;
              if (!el) return;
              const r = el.getBoundingClientRect();
              zoomAt(
                Math.max(MIN_SCALE, scale - 0.5),
                r.left + r.width / 2,
                r.top + r.height / 2
              );
            }}
            className="pz-btn"
            data-testid="pz-zoom-out"
          >
            <ZoomOut className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <span className="pz-scale" data-testid="pz-scale">
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            aria-label="הגדל"
            onClick={() => {
              const el = containerRef.current;
              if (!el) return;
              const r = el.getBoundingClientRect();
              zoomAt(
                Math.min(MAX_SCALE, scale + 0.5),
                r.left + r.width / 2,
                r.top + r.height / 2
              );
            }}
            className="pz-btn"
            data-testid="pz-zoom-in"
          >
            <ZoomIn className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            aria-label="אפס זום"
            onClick={reset}
            className="pz-btn"
            data-testid="pz-zoom-reset"
          >
            <Maximize2 className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      )}
    </div>
  );
}
